import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, BookOpen, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import QAHubCTA from "@/components/QAHubCTA";
import { findQAHubEntry } from "@/data/qaHub";
import { upsertMeta, upsertLink, injectJsonLd, markdownToPlain } from "@/lib/seo";

/**
 * QAHubEntry — one reusable template that renders any /q/:state/:slug
 * page driven by the data file at /app/frontend/src/data/qa_hub_entries.json.
 *
 * Layout (matches existing /q/:slug visual conventions):
 *   - Breadcrumb (Home › Q&A › State › Question)
 *   - H1 (title)
 *   - Bold lead paragraph (shortAnswer)
 *   - Inline CTA
 *   - Body (markdown — simple paragraph + heading + list renderer)
 *   - "What you can do right now" list
 *   - "Related questions" list (internal links)
 *   - "Free help" line
 *   - Disclaimer
 *   - Footer CTA
 *
 * SEO baked in: per-page <title>, meta description, OpenGraph, canonical,
 * FAQPage JSON-LD. All cleaned up on unmount so they don't leak between
 * client-side navigations.
 *
 * When the data array is empty (or the entry is missing) we render a
 * graceful 404 card and let the template stand on its own — the user
 * verified this is the expected behaviour for the initial empty rollout.
 */
export default function QAHubEntry() {
  const { state: stateParam, slug } = useParams();
  const stateUpper = (stateParam || "").toUpperCase();
  const entry = useMemo(() => findQAHubEntry(stateUpper, slug), [stateUpper, slug]);

  // Per-page SEO. Lives in a useEffect so it re-runs when the user navigates
  // between sibling entries via the related-questions list.
  useEffect(() => {
    if (!entry) return;

    const restorers = [];
    const prevTitle = document.title;
    document.title = `${entry.metaTitle || entry.title} · TenantTrail`;

    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const canonicalUrl = `${window.location.origin}/q/${entry.state.toLowerCase()}/${entry.slug}`;

    restorers.push(upsertMeta({ name: "description" }, entry.metaDescription || ""));
    restorers.push(upsertMeta({ property: "og:title" }, entry.title));
    restorers.push(upsertMeta({ property: "og:description" }, entry.metaDescription || entry.shortAnswer));
    restorers.push(upsertMeta({ property: "og:type" }, "article"));
    restorers.push(upsertMeta({ property: "og:url" }, pageUrl));
    restorers.push(upsertMeta({ property: "og:site_name" }, "TenantTrail"));
    restorers.push(upsertMeta({ name: "twitter:card" }, "summary_large_image"));
    restorers.push(upsertMeta({ name: "twitter:title" }, entry.title));
    restorers.push(upsertMeta({ name: "twitter:description" }, entry.metaDescription || entry.shortAnswer));
    restorers.push(upsertLink({ rel: "canonical" }, canonicalUrl));

    // FAQPage JSON-LD — title is the question, shortAnswer + body the answer.
    // "FAQPage" rather than "QAPage" because this template aggregates multiple
    // answer-units (lead, body, doNow) into one canonical Q&A; Google's
    // FAQPage rich result accepts this structure.
    const answerText = [
      entry.shortAnswer,
      markdownToPlain(entry.body),
      entry.doNow?.length ? "Steps: " + entry.doNow.join("; ") : "",
    ].filter(Boolean).join("\n\n");

    restorers.push(injectJsonLd("tt-qahub-faq-jsonld", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [{
        "@type": "Question",
        "name": entry.title,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": answerText,
          "url": canonicalUrl,
        },
      }],
    }));

    // BreadcrumbList JSON-LD so Google renders the path in search results.
    restorers.push(injectJsonLd("tt-qahub-breadcrumb-jsonld", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${window.location.origin}/` },
        { "@type": "ListItem", "position": 2, "name": "Q&A", "item": `${window.location.origin}/q` },
        { "@type": "ListItem", "position": 3, "name": entry.state, "item": `${window.location.origin}/q/${entry.state.toLowerCase()}` },
        { "@type": "ListItem", "position": 4, "name": entry.title, "item": canonicalUrl },
      ],
    }));

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r?.restore?.());
    };
  }, [entry]);

  // Missing-entry guard. We don't bounce to a global /404 because the user
  // may have arrived via a stale link — the inline message lets them try
  // the Q&A index instead.
  if (!entry) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]" data-testid="qa-hub-not-found">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
          <div className="card text-center py-12">
            <AlertTriangle size={28} strokeWidth={1.4} className="mx-auto text-[#B35D4C] mb-3" />
            <h1 className="font-serif text-2xl text-[#0D2B52] mb-2">Question not found</h1>
            <p className="text-sm text-[#6B7280] mb-6">
              We don&apos;t have a /q/{stateUpper.toLowerCase()}/{slug} page yet. browse the full Q&amp;A library.
            </p>
            <Link to="/q" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="qa-hub-not-found-back">
              Browse Q&amp;A library
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="qa-hub-entry">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-10">

        {/* Breadcrumb */}
        <nav
          className="flex items-center gap-1 text-xs text-[#6B7280] mb-6 flex-wrap"
          aria-label="Breadcrumb"
          data-testid="qa-hub-breadcrumb"
        >
          <Link to="/" className="hover:text-[#1A1A1A]">Home</Link>
          <ChevronRight size={11} strokeWidth={1.5} className="text-[#9CA3AF]" />
          <Link to="/q" className="hover:text-[#1A1A1A]">Q&amp;A</Link>
          <ChevronRight size={11} strokeWidth={1.5} className="text-[#9CA3AF]" />
          <span className="font-mono uppercase">{entry.state}</span>
          <ChevronRight size={11} strokeWidth={1.5} className="text-[#9CA3AF]" />
          <span className="text-[#0D2B52] truncate max-w-[40ch]">{entry.title}</span>
        </nav>

        {/* Topic chip + H1 */}
        <div className="mb-4">
          <span
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#1B6C62] bg-[#EFF8F6] px-2 py-1 mb-3"
            data-testid="qa-hub-topic"
          >
            <BookOpen size={10} strokeWidth={1.75} />
            {entry.topic} · {entry.state}
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] leading-tight" data-testid="qa-hub-h1">
            {entry.title}
          </h1>
        </div>

        {/* Bold lead paragraph (shortAnswer) */}
        <p
          className="font-serif text-lg text-[#1A1A1A] leading-relaxed mb-6 font-semibold"
          data-testid="qa-hub-short-answer"
        >
          {entry.shortAnswer}
        </p>

        {/* Inline CTA (1 of 2) */}
        <QAHubCTA contextual={entry.ctaContextual} variant="inline" />

        {/* Body */}
        {entry.body && (
          <div
            className="prose-tt mt-6 mb-8 space-y-4 text-[#333] leading-relaxed"
            data-testid="qa-hub-body"
          >
            <MarkdownBlock md={entry.body} />
          </div>
        )}

        {/* What you can do right now */}
        {entry.doNow && entry.doNow.length > 0 && (
          <section className="card mb-6" data-testid="qa-hub-do-now">
            <h2 className="font-serif text-xl text-[#0D2B52] mb-3">What you can do right now</h2>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-[#333] leading-relaxed">
              {entry.doNow.map((step, i) => (
                <li key={i} data-testid={`qa-hub-do-now-${i}`}>{step}</li>
              ))}
            </ol>
          </section>
        )}

        {/* Related questions */}
        {entry.related && entry.related.length > 0 && (
          <section className="card mb-6" data-testid="qa-hub-related">
            <h2 className="font-serif text-xl text-[#0D2B52] mb-3">Related questions</h2>
            <ul className="space-y-1.5 text-sm">
              {entry.related.map((r, i) => (
                <li key={i}>
                  <Link
                    to={r.href}
                    className="text-[#1B6C62] hover:underline"
                    data-testid={`qa-hub-related-${i}`}
                  >
                    {r.label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Free help line */}
        {entry.freeHelp && entry.freeHelp.length > 0 && (
          <section className="card mb-6 bg-[#FFF4EC] border-[#F0CDB8]" data-testid="qa-hub-free-help">
            <h2 className="font-serif text-base text-[#0D2B52] mb-2">Free help</h2>
            <ul className="space-y-1 text-xs text-[#333] leading-relaxed">
              {entry.freeHelp.map((line, i) => (
                <li key={i} data-testid={`qa-hub-free-help-${i}`}>{line}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Disclaimer */}
        <p
          className="text-xs italic text-[#6B7280] my-6 leading-relaxed"
          data-testid="qa-hub-disclaimer"
        >
          General information, not legal advice.
        </p>

        {/* Footer CTA (2 of 2) */}
        <QAHubCTA contextual={entry.ctaContextual} variant="footer" />

      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Tiny markdown renderer for the body. Keeps the template free of any new
 * dependencies. Handles paragraphs, headings (## and ###), bullet lists,
 * **bold** and _italic_. If you need full markdown later, swap in
 * react-markdown — every render call site uses this MarkdownBlock so it's
 * a single-file change.
 */
function MarkdownBlock({ md }) {
  if (!md) return null;
  const blocks = md.split(/\n\n+/);
  return (
    <>
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (trimmed.startsWith("### ")) {
          return <h3 key={i} className="font-serif text-lg text-[#0D2B52] mt-4">{trimmed.slice(4)}</h3>;
        }
        if (trimmed.startsWith("## ")) {
          return <h2 key={i} className="font-serif text-xl text-[#0D2B52] mt-4">{trimmed.slice(3)}</h2>;
        }
        if (/^[-*+]\s/m.test(trimmed)) {
          const items = trimmed.split(/\n/).map((l) => l.replace(/^[-*+]\s+/, ""));
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 text-sm">
              {items.map((item, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inlineFmt(item) }} />)}
            </ul>
          );
        }
        return <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFmt(trimmed) }} />;
      })}
    </>
  );
}

function inlineFmt(s) {
  // Order matters: bold (** **) must be consumed BEFORE single-asterisk
  // italic (* *), otherwise *foo* inside **foo** would corrupt the markup.
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*\n]+)\*/g, "<em>$1</em>")
    .replace(/_([^_]+)_/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, '<code class="font-mono text-[12px] bg-[#EFEDE5] px-1">$1</code>');
}

// MarkdownBlock is internal to this module; no exports needed.
